import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

function ordenarEmpresas(empresas) {
  if (!Array.isArray(empresas)) return [];
  return [...empresas].sort((a, b) => String(a?.nombre || '').localeCompare(String(b?.nombre || ''), 'es', { sensitivity: 'base' }));
}

export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [empresasDisponibles, setEmpresasDisponibles] = useState([]);
  const [cargando, setCargando] = useState(true);
  // Empresa activa persistida en frontend para recordar el último contexto.
  const [empresaActivaId, setEmpresaActivaId] = useState(() => localStorage.getItem('trix_empresa_activa') || '');

  const aplicarPayloadAuth = useCallback((data) => {
    const empresaId = data?.empresaActivaId || data?.empresa?._id || '';
    setUsuario(data?.usuario || null);
    setEmpresa(data?.empresa || null);
    setEmpresasDisponibles(ordenarEmpresas(data?.empresasDisponibles || []));
    setEmpresaActivaId(empresaId);

    if (empresaId) {
      localStorage.setItem('trix_empresa_activa', empresaId);
    } else {
      localStorage.removeItem('trix_empresa_activa');
    }
  }, []);

  // Carga el perfil a partir del token guardado
  const cargarPerfil = useCallback(async () => {
    const token = localStorage.getItem('trix_token');
    if (!token) {
      setCargando(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      aplicarPayloadAuth(data);
    } catch (err) {
      localStorage.removeItem('trix_token');
      localStorage.removeItem('trix_empresa_activa');
      setUsuario(null);
      setEmpresa(null);
      setEmpresasDisponibles([]);
      setEmpresaActivaId('');
    } finally {
      setCargando(false);
    }
  }, [aplicarPayloadAuth]);

  useEffect(() => {
    cargarPerfil();
  }, [cargarPerfil]);

  const login = async (email, password, empresaId = '') => {
    const payload = { email, password };
    if (empresaId) payload.empresaId = empresaId;
    const { data } = await api.post('/auth/login', payload);
    localStorage.setItem('trix_token', data.token);
    aplicarPayloadAuth(data);
    return data;
  };

  const switchEmpresa = async (nuevaEmpresaId) => {
    if (!nuevaEmpresaId) return;
    const { data } = await api.post('/auth/switch-empresa', { empresaId: nuevaEmpresaId });
    localStorage.setItem('trix_token', data.token);
    aplicarPayloadAuth(data);

    // Recarga completa para limpiar vistas, sesiones y cachés en memoria.
    window.location.assign('/app');
  };

  const logout = () => {
    localStorage.removeItem('trix_token');
    localStorage.removeItem('trix_empresa_activa');
    setUsuario(null);
    setEmpresa(null);
    setEmpresasDisponibles([]);
    setEmpresaActivaId('');
  };

  const value = {
    usuario,
    empresa,
    empresasDisponibles,
    cargando,
    empresaActivaId,
    login,
    switchEmpresa,
    logout,
    recargar: cargarPerfil,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}
